import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { DataService } from '../../Services/dataService'; 
import { Modal, Button, Table, Form, Row, Col } from 'react-bootstrap-v5';

const DiscountSettings = () => {
    const [rules, setRules] = useState([]);
    const [countries, setCountries] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [loading, setLoading] = useState(false);

    const initialFormState = {
        type: 'Volume Discount',
        mode: 'Courier',
        condition_type: 'Weight',
        condition_value: '',
        discount_type: 'Percentage',
        discount_value: '',
        valid_from: '',
        valid_to: '',
        is_active: true
    };
    const [formData, setFormData] = useState(initialFormState);

    useEffect(() => {
        fetchRules();
        fetchCountries();
    }, []);

    const fetchCountries = async () => {
        try {
            const res = await DataService.get('/admin/get_country');
            if (res.data && Array.isArray(res.data)) {
                setCountries(res.data);
            } else if (res.data && Array.isArray(res.data.data)) {
                setCountries(res.data.data);
            }
        } catch (error) {
            console.error("Failed to load countries", error);
        }
    };

    const fetchRules = async () => {
        setLoading(true);
        try {
            const res = await DataService.get('/admin/discounts');
            if (res.success) {
                setRules(res.data);
            }
        } catch (error) {
            toast.error("Failed to load discount rules");
        } finally {
            setLoading(false);
        }
    };

    const handleShow = (rule = null) => {
        if (rule) {
            setEditingRule(rule);
            setFormData({
                ...rule,
                valid_from: rule.valid_from ? rule.valid_from.split('T')[0] : '',
                valid_to: rule.valid_to ? rule.valid_to.split('T')[0] : ''
            });
        } else {
            setEditingRule(null);
            setFormData(initialFormState);
        }
        setShowModal(true);
    };

    const handleClose = () => {
        setShowModal(false);
        setEditingRule(null);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    };

    const handleSave = async () => {
        if (!formData.discount_value) {
            toast.error("Please fill in all required fields (Discount Value).");
            return;
        }
        if (formData.condition_type && !formData.condition_value) {
            toast.error("Please provide a condition value for the selected condition type.");
            return;
        }
        
        try {
            setLoading(true);
            if (editingRule) {
                const res = await DataService.put(`/admin/discounts/${editingRule._id}`, formData);
                if (res.success) toast.success("Rule updated");
            } else {
                const res = await DataService.post('/admin/discounts', formData);
                if (res.success) toast.success("Rule created");
            }
            handleClose();
            fetchRules();
        } catch (error) {
            toast.error("Error saving rule");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure?")) return;
        try {
            const res = await DataService.delete(`/admin/discounts/${id}`);
            if (res.success) toast.success("Rule deleted");
            fetchRules();
        } catch (error) {
            toast.error("Error deleting rule");
        }
    };

    return (
        <div className="card mt-4">
            <div className="card-header d-flex justify-content-between align-items-center">
                <h5>Discount Settings Engine</h5>
                <Button variant="primary" onClick={() => handleShow()}>Add New Discount Rule</Button>
            </div>
            <div className="card-body">
                <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            <th>Type</th>
                            <th>Mode</th>
                            <th>Condition</th>
                            <th>Value</th>
                            <th>Discount</th>
                            <th>Validity</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rules.map((rule, idx) => (
                            <tr key={idx}>
                                <td>{rule.type}</td>
                                <td>{rule.mode}</td>
                                <td>{rule.condition_type || '-'}</td>
                                <td>{rule.condition_value || '-'}</td>
                                <td>{rule.discount_type === 'Flat Amount' ? `AED ${rule.discount_value}` : `${rule.discount_value}% on Frt`}</td>
                                <td>{(rule.valid_from || rule.valid_to) ? `${rule.valid_from ? new Date(rule.valid_from).toLocaleDateString() : 'Always'} - ${rule.valid_to ? new Date(rule.valid_to).toLocaleDateString() : 'Forever'}` : 'No Expiry'}</td>
                                <td>{rule.is_active ? 'Active' : 'Inactive'}</td>
                                <td>
                                    <Button variant="info" size="sm" className="mr-2" onClick={() => handleShow(rule)}>Edit</Button>
                                    <Button variant="danger" size="sm" onClick={() => handleDelete(rule._id)}>Delete</Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            <Modal show={showModal} onHide={handleClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{editingRule ? 'Edit Discount Rule' : 'Add Discount Rule'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Discount Type</Form.Label>
                                    <Form.Control as="select" name="type" value={formData.type} onChange={handleChange}>
                                        <option value="Volume Discount">Volume Discount</option>
                                        <option value="Promotion Discount">Promotion Discount</option>
                                        <option value="Product Specific Discount">Product Specific Discount</option>
                                        <option value="Promo Code Discount">Promo Code Discount</option>
                                        <option value="Country Specific Discount">Country Specific Discount</option>
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Mode of Transport</Form.Label>
                                    <Form.Control as="select" name="mode" value={formData.mode} onChange={handleChange}>
                                        <option value="Courier">Courier</option>
                                        <option value="Air">Air</option>
                                        <option value="Sea">Sea</option>
                                        <option value="Land">Land</option>
                                        <option value="All Modes">All Modes</option>
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Condition Type</Form.Label>
                                    <Form.Control as="select" name="condition_type" value={formData.condition_type} onChange={handleChange}>
                                        <option value="">No Condition</option>
                                        <option value="Weight">Weight (Exact kgs)</option>
                                        <option value="CBM">Volume (Exact cbm)</option>
                                        <option value="HSCode">HS Codes (Comma separated)</option>
                                        <option value="PromoCode">Promo Code Name</option>
                                        <option value="Country">Country Name</option>
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Condition Value {formData.condition_type && <span className="text-danger">*</span>}</Form.Label>
                                    {formData.condition_type === 'Weight' ? (
                                        <Form.Control type="number" name="condition_value" value={formData.condition_value} onChange={handleChange} placeholder="e.g. 10 (Weight in kg)" />
                                    ) : formData.condition_type === 'CBM' ? (
                                        <Form.Control type="number" name="condition_value" value={formData.condition_value} onChange={handleChange} placeholder="e.g. 5 (Volume in cbm)" />
                                    ) : formData.condition_type === 'HSCode' ? (
                                        <Form.Control type="text" name="condition_value" value={formData.condition_value} onChange={handleChange} placeholder="e.g. 1234.56, 7890.12 (comma separated)" />
                                    ) : formData.condition_type === 'PromoCode' ? (
                                        <Form.Control type="text" name="condition_value" value={formData.condition_value} onChange={handleChange} placeholder="e.g. SUMMER50" />
                                    ) : formData.condition_type === 'Country' ? (
                                        <Form.Control as="select" name="condition_value" value={formData.condition_value} onChange={handleChange}>
                                            <option value="">Select Country</option>
                                            {countries.map((c, i) => (
                                                <option key={i} value={c.country_name || c.name}>{c.country_name || c.name}</option>
                                            ))}
                                        </Form.Control>
                                    ) : (
                                        <Form.Control type="text" name="condition_value" value={formData.condition_value} onChange={handleChange} placeholder="Select a condition type first" disabled={!formData.condition_type} />
                                    )}
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Discount Type (Percentage/Flat)</Form.Label>
                                    <Form.Control as="select" name="discount_type" value={formData.discount_type || 'Percentage'} onChange={handleChange}>
                                        <option value="Percentage">Percentage</option>
                                        <option value="Flat Amount">Flat Amount</option>
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Discount Value <span className="text-danger">*</span></Form.Label>
                                    <Form.Control type="number" name="discount_value" value={formData.discount_value} onChange={handleChange} />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Valid From</Form.Label>
                                    <Form.Control type="date" name="valid_from" value={formData.valid_from} onChange={handleChange} />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group>
                                    <Form.Label>Valid To</Form.Label>
                                    <Form.Control type="date" name="valid_to" value={formData.valid_to} onChange={handleChange} />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mt-2">
                            <Form.Check type="checkbox" label="Is Active" name="is_active" checked={formData.is_active} onChange={handleChange} />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>Cancel</Button>
                    <Button variant="primary" onClick={handleSave} disabled={loading}>{loading ? 'Saving...' : 'Save Rule'}</Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default DiscountSettings;
